import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, Phone, Mail, MessageCircle, ArrowUpDown } from "lucide-react";
import { Pill } from "@/components/perf";
import MessagesPanel from "@/components/MessagesPanel";

const input = "bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red w-full";

export default function CoachClientList({ isCoachOnly, onSwap }) {
  const [clients, setClients] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [dmUser, setDmUser] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/coach/clients");
      setClients(data);
    } catch { setClients(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Enter the client's name");
    setSaving(true);
    try {
      await api.post("/coach/clients", {
        name: form.name, email: form.email || null, phone: form.phone || null });
      toast.success(`${form.name} added to your client list`);
      setForm({ name: "", email: "", phone: "" });
      setShowAdd(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add client"); }
    finally { setSaving(false); }
  };

  const remove = async (cl) => {
    if (!window.confirm(`Remove ${cl.name} from the client list?`)) return;
    try {
      await api.delete(`/coach/clients/${cl.id}`);
      toast.success("Client removed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (clients === false) return null;

  return (
    <div className="bg-pb-surface border border-pb-border" data-testid="coach-client-list">
      <div className="flex items-center justify-between p-5 border-b border-pb-border">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Client List ({clients?.length ?? "…"})</span>
        </div>
        <div className="flex items-center gap-2">
          {onSwap && (
            <button data-testid="console-swap-btn" onClick={onSwap} title="Swap position with Scheduled Sessions"
              className="w-7 h-7 flex items-center justify-center border border-pb-border text-zinc-500 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
              <ArrowUpDown className="w-3.5 h-3.5" />
            </button>
          )}
          {isCoachOnly && (
            <button data-testid="add-client-toggle" onClick={() => setShowAdd(!showAdd)}
              className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-[#BEF264] active:scale-95 transition-all">
              <Plus className="w-3.5 h-3.5" /> {showAdd ? "Hide" : "Add Client"}
            </button>
          )}
        </div>
      </div>
      {showAdd && (
        <div className="p-4 border-b border-pb-border bg-pb-elevated/30 grid sm:grid-cols-4 gap-2 animate-fade-up" data-testid="add-client-form">
          <input data-testid="client-name" value={form.name} placeholder="Name *"
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          <input data-testid="client-email" value={form.email} placeholder="Email"
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
          <input data-testid="client-phone" value={form.phone} placeholder="Phone"
            onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} />
          <button data-testid="client-save" onClick={add} disabled={saving}
            className="bg-pb-red text-black font-bold uppercase tracking-widest text-xs py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            {saving ? "Adding…" : "Save"}
          </button>
        </div>
      )}
      <div className="divide-y divide-pb-border/40 max-h-[240px] overflow-y-auto" data-testid="client-scroll-list">
        {clients === null ? (
          <div className="p-4 text-sm text-zinc-500 animate-pulse">Loading clients…</div>
        ) : clients.length === 0 ? (
          <div className="p-4 text-sm text-zinc-600" data-testid="clients-empty">
            No clients yet — members are added automatically when they book your sessions, or add one manually.
          </div>
        ) : clients.map((cl) => (
          <div key={cl.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`client-row-${cl.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                {cl.name}
                <Pill color={cl.source === "booking" ? "#22D3EE" : "#A3E635"}>{cl.source === "booking" ? "Booked" : "Manual"}</Pill>
                {cl.coach_name && <span className="text-[10px] text-zinc-500 uppercase">→ {cl.coach_name}</span>}
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap mt-0.5">
                {cl.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{cl.phone}</span>}
                {cl.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{cl.email}</span>}
                {!cl.phone && !cl.email && <span className="text-zinc-600">No contact info</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {cl.user_id && (
                <button data-testid={`client-message-${cl.id}`} onClick={() => setDmUser({ id: cl.user_id, name: cl.name })} title={`Message ${cl.name}`}
                  className="w-7 h-7 flex items-center justify-center border border-pb-border hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                  <MessageCircle className="w-3.5 h-3.5" />
                </button>
              )}
              <button data-testid={`client-delete-${cl.id}`} onClick={() => remove(cl)} title="Remove client"
                className="w-7 h-7 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <MessagesPanel open={!!dmUser} initialUser={dmUser} onClose={() => setDmUser(null)} />
    </div>
  );
}
